import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, CloudDownload as IngestIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { usePermission } from '../hooks/usePermission';
import AgentSettings from '../components/Settings/AgentSettings';
import AgentConfigurationDialog from '../components/Settings/AgentConfigurationDialog';
import EndpointsTab from '../components/Integrations/EndpointsTab';
import ActivityTab from '../components/Integrations/ActivityTab';
import AnalyticsTab from '../components/Integrations/AnalyticsTab';
import ThreatIntelTab from '../components/Integrations/ThreatIntelTab';
import IndicatorLookup from '../components/Integrations/IndicatorLookup';
import EmptyState from '../components/Integrations/EmptyState';
import { describeApiError } from '../components/Integrations/agentMeta';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`integrations-tabpanel-${index}`} aria-labelledby={`integrations-tab-${index}`}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Integrations = () => {
  const { canManageAgents } = usePermission();
  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestAllResult, setIngestAllResult] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [agentsRes, typesRes] = await Promise.all([apiService.agents.getAll(), apiService.agents.getTypes()]);
      setAgents(agentsRes.data.agents || []);
      setTypes(typesRes.data.types || []);
      setError(null);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load integrations').message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleIngestAll = async () => {
    try {
      setIngesting(true);
      const res = await apiService.agents.ingestAll();
      setIngestAllResult(res.data);
      await load();
    } catch (err) {
      setError(describeApiError(err, 'Ingestion failed').message);
    } finally {
      setIngesting(false);
    }
  };

  const enabledIngestible = agents.filter((a) => a.isEnabled).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h4">Integrations</Typography>
          <Typography variant="body2" color="text.secondary">
            EDR agents, endpoint inventory, activity and threat-intelligence lookups.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Reload">
            <span>
              <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={load} disabled={loading}>
                Refresh
              </Button>
            </span>
          </Tooltip>
          {canManageAgents && agents.length > 0 && (
            <>
              <Tooltip title={enabledIngestible ? 'Run alert ingestion for every enabled agent now' : 'No enabled agents'}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={ingesting ? <CircularProgress size={16} /> : <IngestIcon />}
                    onClick={handleIngestAll}
                    disabled={ingesting || !enabledIngestible}
                  >
                    Ingest from all
                  </Button>
                </span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add integration</Button>
            </>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && agents.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={agents.length ? `Agents (${agents.length})` : 'Agents'} id="integrations-tab-0" />
            <Tab label="Endpoints" id="integrations-tab-1" />
            <Tab label="Activity" id="integrations-tab-2" />
            <Tab label="Analytics" id="integrations-tab-3" />
            <Tab label="Threat intel" id="integrations-tab-4" />
          </Tabs>

          <TabPanel value={tab} index={0}>
            {agents.length === 0
              ? <EmptyState types={types} canManage={canManageAgents} onAdd={() => setAddOpen(true)} />
              : <AgentSettings agents={agents} types={types} onReload={load} loading={loading} hideHeader />}
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <EndpointsTab agents={agents} types={types} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <ActivityTab agents={agents} />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <AnalyticsTab agents={agents} types={types} />
          </TabPanel>
          <TabPanel value={tab} index={4}>
            <ThreatIntelTab />
          </TabPanel>

          <Box sx={{ mt: 4 }}>
            <IndicatorLookup />
          </Box>
        </>
      )}

      <AgentConfigurationDialog
        open={addOpen}
        agent={null}
        onClose={(saved) => { setAddOpen(false); if (saved) load(); }}
      />

      {/* Ingest-all results */}
      <Dialog open={!!ingestAllResult} onClose={() => setIngestAllResult(null)} maxWidth="md" fullWidth>
        <DialogTitle>Ingestion results</DialogTitle>
        <DialogContent>
          {ingestAllResult && (
            <>
              <Alert severity={ingestAllResult.summary && ingestAllResult.summary.failedAgents ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {ingestAllResult.message}
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell align="right">Alerts</TableCell>
                    <TableCell align="right">Created</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(ingestAllResult.results || []).map((r) => (
                    <TableRow key={r.agentId || r.agentName}>
                      <TableCell>{r.agentName}</TableCell>
                      <TableCell>
                        {r.skipped ? (
                          <Chip size="small" variant="outlined" label={`skipped${r.reason ? ` – ${r.reason}` : ''}`} />
                        ) : (
                          <Chip size="small" color={r.success ? 'success' : 'error'} label={r.success ? 'success' : 'failed'} />
                        )}
                      </TableCell>
                      <TableCell align="right">{r.alertsProcessed ?? '—'}</TableCell>
                      <TableCell align="right">{r.detectionsCreated ?? '—'}</TableCell>
                      <TableCell align="right">{r.detectionsUpdated ?? '—'}</TableCell>
                      <TableCell>
                        {r.error && <Typography variant="body2" color="error">{r.error}</Typography>}
                        {Array.isArray(r.errors) && r.errors.length > 0 && (
                          <Typography variant="body2" color="error">
                            {r.errors.slice(0, 5).map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e))).join('; ')}
                            {r.errors.length > 5 ? ` (+${r.errors.length - 5} more)` : ''}
                          </Typography>
                        )}
                        {!r.error && !(r.errors && r.errors.length) && '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIngestAllResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
